import { useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, ExternalLinkIcon, GlobeIcon, XIcon } from 'lucide-react';
import type { Provider, ProviderEnrichmentResult } from '@kennisbank/shared';
import { ApiError, apiPost } from '../api/client.js';

/**
 * "Verrijk vanuit website" diff modal.
 *
 * Linda's flow:
 *   1. Click "Verrijk vanuit website" on a provider card
 *   2. Loading spinner (10-30s while backend crawls + Gemini extracts)
 *   3. Side-by-side diff: every field that the bot proposes shows current
 *      value left, proposed value right, source quote + clickable URL.
 *   4. One "Accepteer" button — accepts ALL non-null proposed fields, or
 *      "Annuleer" to discard.
 *   5. On Accepteer, PATCHes via apply-enrichment, audit row written
 *      backend-side, modal closes, parent refreshes the list.
 *
 * Per design-doc decision: single Accepteer (no per-field checkboxes).
 * Extractor is conservative — null > low-confidence guess — so Linda can
 * trust that every non-null value is one the bot was reasonably sure about.
 */

interface Props {
  provider: Provider;
  onClose: () => void;
  onApplied: () => void;
}

type FieldKey = keyof ProviderEnrichmentResult['fields'];

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string; code?: string }
  | {
      kind: 'preview';
      data: ProviderEnrichmentResult;
      /** Which proposed fields Linda has currently selected for apply.
       *  Default = every field where Gemini returned a non-null value.
       *  She toggles per row to exclude. */
      selected: Set<FieldKey>;
      applying: boolean;
    };

const FIELD_LABELS: Record<keyof ProviderEnrichmentResult['fields'], string> = {
  name: 'Naam',
  type: 'Type',
  address: 'Adres',
  city: 'Plaats',
  region: 'Regio',
  province: 'Provincie',
  phone: 'Telefoon',
  email: 'E-mail',
  website: 'Website',
  specialisations: 'Specialisaties',
  therapyTypes: 'Therapievormen',
  targetAudience: 'Doelgroep',
  status: 'Status',
  waitingTime: 'Wachttijd',
  notes: 'Notities',
};

export function EnrichmentDiffModal({ provider, onClose, onApplied }: Props): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const data = await apiPost<ProviderEnrichmentResult>(
          `/api/v1/providers/${provider.id}/enrich-preview`,
        );
        if (!cancelled) {
          // Default-select every field with a non-null value.
          const selected = new Set<FieldKey>();
          (Object.keys(data.fields) as FieldKey[]).forEach((k) => {
            if (data.fields[k].value !== null) selected.add(k);
          });
          setState({ kind: 'preview', data, selected, applying: false });
        }
      } catch (err) {
        if (cancelled) return;
        const apiErr = err instanceof ApiError ? err : null;
        let message = 'Verrijking mislukt. Probeer opnieuw.';
        if (apiErr?.code === 'no_website') {
          message = 'Deze provider heeft geen website ingesteld.';
        } else if (apiErr?.code === 'robots_disallowed') {
          message = 'Deze website staat verrijking niet toe.';
        } else if (apiErr?.code === 'unreachable') {
          message = 'Website niet bereikbaar. Probeer later opnieuw.';
        } else if (apiErr?.code === 'extractor_failed') {
          message = 'De extractor kon de website niet lezen. Probeer opnieuw.';
        }
        setState({ kind: 'error', message, code: apiErr?.code });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [provider.id]);

  function toggleField(key: FieldKey): void {
    if (state.kind !== 'preview') return;
    if (state.applying) return;
    const next = new Set(state.selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setState({ ...state, selected: next });
  }

  async function accept(): Promise<void> {
    if (state.kind !== 'preview') return;
    if (state.selected.size === 0) return;
    setState({ ...state, applying: true });

    // Build a clone of the preview where any non-selected field's value is
    // forced to null. The backend's buildProviderPatch ignores null fields,
    // so this gives us per-field accept without changing the apply contract.
    // Type-erase via Record because each FieldKey points at a distinct
    // discriminated-union member; tsc loses sight of that across the loop.
    const filteredFields: Record<string, unknown> = { ...state.data.fields };
    (Object.keys(filteredFields) as FieldKey[]).forEach((k) => {
      if (!state.selected.has(k)) {
        filteredFields[k] = {
          ...(filteredFields[k] as Record<string, unknown>),
          value: null,
          confidence: 0,
          sourceQuote: null,
          sourcePageUrl: null,
        };
      }
    });

    try {
      await apiPost(`/api/v1/providers/${provider.id}/apply-enrichment`, {
        preview: {
          ...state.data,
          fields: filteredFields as ProviderEnrichmentResult['fields'],
        },
      });
      onApplied();
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Toepassen mislukt. Probeer opnieuw.',
      });
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="enrich-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4"
    >
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-brand-100 px-6 py-4">
          <div className="flex items-center gap-2">
            <GlobeIcon size={18} className="text-brand-700" />
            <h2 id="enrich-title" className="text-lg font-semibold text-ink-900">
              Verrijk vanuit website
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluiten"
            className="rounded-md p-1 text-ink-900/60 hover:bg-brand-50 hover:text-ink-900"
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-4">
          {state.kind === 'loading' && (
            <div className="flex items-center justify-center py-16 text-ink-900/60">
              <div className="text-center">
                <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-brand-200 border-t-brand-700" />
                <p className="text-sm">
                  Bezig met verrijken vanuit{' '}
                  <span className="font-medium">{provider.contactInfo.website}</span>…
                </p>
                <p className="mt-1 text-xs text-ink-900/40">Dit duurt ~10-30 seconden.</p>
              </div>
            </div>
          )}

          {state.kind === 'error' && (
            <div className="py-10 text-center">
              <p className="text-sm text-red-700">{state.message}</p>
              <button
                type="button"
                onClick={onClose}
                className="mt-4 rounded-md border border-brand-200 px-4 py-1.5 text-sm text-ink-900 hover:bg-brand-50"
              >
                Sluiten
              </button>
            </div>
          )}

          {state.kind === 'preview' && (
            <DiffTable
              provider={provider}
              preview={state.data}
              selected={state.selected}
              onToggle={toggleField}
            />
          )}
        </div>

        {state.kind === 'preview' && (
          <div className="flex items-center justify-between border-t border-brand-100 bg-brand-50/30 px-6 py-3">
            <p className="text-[11px] text-ink-900/50">
              {state.data.pagesCrawled.length} pagina&apos;s gelezen
              {state.data.pagesSkipped.length > 0
                ? `, ${state.data.pagesSkipped.length} overgeslagen`
                : ''}{' '}
              · duur {(state.data.durationMs / 1000).toFixed(1)}s
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={state.applying}
                className="rounded-md border border-brand-200 px-4 py-1.5 text-sm text-ink-900 transition-colors hover:bg-brand-50 disabled:opacity-50"
              >
                Annuleer
              </button>
              <button
                type="button"
                onClick={accept}
                disabled={state.applying || state.selected.size === 0}
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-50"
              >
                <CheckIcon size={14} />
                {state.applying
                  ? 'Bezig…'
                  : `Accepteer ${state.selected.size} ${state.selected.size === 1 ? 'veld' : 'velden'}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DiffTable({
  provider,
  preview,
  selected,
  onToggle,
}: {
  provider: Provider;
  preview: ProviderEnrichmentResult;
  selected: Set<FieldKey>;
  onToggle: (key: FieldKey) => void;
}): JSX.Element {
  const rows = useMemo(
    () =>
      (Object.keys(preview.fields) as FieldKey[]).map((key) => ({
        key,
        label: FIELD_LABELS[key],
        current: currentValueFor(provider, key),
        proposed: preview.fields[key],
      })),
    [provider, preview],
  );

  const proposedRows = rows.filter((r) => r.proposed.value !== null);
  if (proposedRows.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-ink-900/60">
        De extractor heeft niets met voldoende vertrouwen kunnen vinden om voor te stellen. Probeer
        eventueel een andere website, of bewerk handmatig.
      </div>
    );
  }

  return (
    <table className="w-full table-fixed text-sm">
      <colgroup>
        <col className="w-10" />
        <col className="w-32" />
        <col />
        <col className="w-[45%]" />
      </colgroup>
      <thead className="text-left text-[11px] uppercase tracking-wider text-ink-800/60">
        <tr>
          <th className="py-2"></th>
          <th className="py-2 pr-4">Veld</th>
          <th className="py-2 pr-4">Huidig</th>
          <th className="py-2">Voorstel (vertrouwen · bron)</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-brand-50">
        {proposedRows.map((r) => {
          const on = selected.has(r.key);
          return (
            <tr
              key={r.key}
              className={`align-top transition-colors ${
                on ? '' : 'bg-slate-50/60 text-ink-900/40'
              }`}
            >
              <td className="py-2 pr-2">
                <button
                  type="button"
                  onClick={() => onToggle(r.key)}
                  aria-pressed={on}
                  aria-label={on ? `Wijs ${r.label} af` : `Selecteer ${r.label}`}
                  title={on ? 'Klik om uit te sluiten' : 'Klik om op te nemen'}
                  className={`flex h-6 w-6 items-center justify-center rounded-md border transition-colors ${
                    on
                      ? 'border-brand-700 bg-brand-700 text-white hover:bg-brand-800'
                      : 'border-brand-200 bg-white text-transparent hover:border-brand-400'
                  }`}
                >
                  <CheckIcon size={14} />
                </button>
              </td>
              <td className="break-words py-2 pr-4 font-medium text-ink-900/80">{r.label}</td>
              <td className="break-words py-2 pr-4 text-ink-900/60">
                <span className="line-clamp-3">{formatValue(r.current)}</span>
              </td>
              <td className="py-2">
                <div
                  className={`break-words ${on ? 'font-medium text-ink-900' : 'font-medium text-ink-900/40'}`}
                >
                  <span className="line-clamp-6">{formatValue(r.proposed.value)}</span>
                </div>
                <div className="mt-0.5 text-[11px] text-ink-900/50">
                  {(r.proposed.confidence * 100).toFixed(0)}% ·{' '}
                  {r.proposed.sourcePageUrl ? (
                    <a
                      href={r.proposed.sourcePageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 text-brand-700 hover:underline"
                    >
                      bron <ExternalLinkIcon size={10} />
                    </a>
                  ) : (
                    <span className="text-ink-900/30">—</span>
                  )}
                </div>
                {r.proposed.sourceQuote && (
                  <div className="mt-1 line-clamp-2 rounded bg-brand-50/50 px-2 py-1 text-[11px] italic text-ink-900/60">
                    &ldquo;{r.proposed.sourceQuote}&rdquo;
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function currentValueFor(p: Provider, key: keyof ProviderEnrichmentResult['fields']): unknown {
  switch (key) {
    case 'name':
      return p.name;
    case 'type':
      return p.type;
    case 'address':
      return p.location.address;
    case 'city':
      return p.location.city;
    case 'region':
      return p.location.region;
    case 'province':
      return p.location.province;
    case 'phone':
      return p.contactInfo.phone ?? null;
    case 'email':
      return p.contactInfo.email ?? null;
    case 'website':
      return p.contactInfo.website ?? null;
    case 'specialisations':
      return p.specialisations;
    case 'therapyTypes':
      return p.therapyTypes;
    case 'targetAudience':
      return p.targetAudience;
    case 'status':
      return p.status;
    case 'waitingTime':
      return p.waitingTime;
    case 'notes':
      return p.notes ?? null;
  }
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.length === 0 ? '—' : v.join(', ');
  return String(v);
}
